import { Box, BoxProps, Divider, Link, SxProps, Theme, Typography } from "@mui/material";
import React from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import MarkdownCodeBlock from "./MarkdownCodeBlock";

interface StyledMarkdownProps extends Omit<BoxProps, "children"> {
  children?: string;
  sx?: SxProps<Theme>;
  variant?: "document" | "chat";
}

const StyledMarkdown: React.FC<StyledMarkdownProps> = ({ children, sx, variant = "document", ...rest }) => (
  <Box sx={sx} {...rest}>
    <Markdown
      skipHtml
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => (
          <Typography variant="body1" sx={{ mt: 0.5, mb: 0.5 }}>
            {children}
          </Typography>
        ),
        h1: ({ children }) => (
          <Typography component="h1" variant={variant === "chat" ? "h6" : "h4"} gutterBottom>
            {children}
          </Typography>
        ),
        h2: ({ children }) => (
          <Typography component="h2" variant={variant === "chat" ? "subtitle1" : "h5"} gutterBottom>
            {children}
          </Typography>
        ),
        h3: ({ children }) => (
          <Typography component="h3" variant={variant === "chat" ? "subtitle2" : "h6"} gutterBottom>
            {children}
          </Typography>
        ),
        h4: ({ children }) => (
          <Typography component="h4" variant="subtitle1" gutterBottom>
            {children}
          </Typography>
        ),
        h5: ({ children }) => (
          <Typography component="h5" variant="subtitle2" gutterBottom>
            {children}
          </Typography>
        ),
        h6: ({ children }) => (
          <Typography component="h6" variant="subtitle2" gutterBottom>
            {children}
          </Typography>
        ),
        li: ({ children }) => (
          <Typography variant="body1" component="li">
            {children}
          </Typography>
        ),
        hr: () => <Divider sx={{ my: 1 }} />,
        code: ({ children, className }) => (
          <Typography
            variant="body2"
            component="code"
            className={className}
            sx={{ fontFamily: "monospace", bgcolor: "action.hover", px: 0.5, borderRadius: 0.5 }}
          >
            {children}
          </Typography>
        ),
        a: ({ href, children }) => href ? (
          <Link href={href} target="_blank" rel="noopener noreferrer" underline="always" sx={{ overflowWrap: "anywhere" }}>
            {children}
          </Link>
        ) : <span>{children}</span>,
        strong: ({ children }) => (
          <Typography variant="inherit" component="strong" sx={{ fontWeight: "bold" }}>
            {children}
          </Typography>
        ),
        em: ({ children }) => (
          <Typography variant="inherit" component="em" sx={{ fontStyle: "italic" }}>
            {children}
          </Typography>
        ),
        blockquote: ({ children }) => (
          <Typography component="blockquote" sx={{ borderLeft: 4, borderColor: "divider", pl: 2, ml: 0, my: 1, color: "text.secondary" }}>
            {children}
          </Typography>
        ),
        pre: MarkdownCodeBlock,
      }}
    >
      {children}
    </Markdown>
  </Box>
);

/** Small, stable SDK surface: generated code cannot supply parser plugins or raw HTML options. */
export type SdkStyledMarkdownProps = Readonly<{
  children: string;
  className?: string;
  variant?: "document" | "chat";
}>;

export function SdkStyledMarkdown({ children, className, variant = "document" }: SdkStyledMarkdownProps) {
  return (
    <StyledMarkdown className={className} variant={variant}>
      {children}
    </StyledMarkdown>
  );
}

export default StyledMarkdown;
